import { useCallback, useEffect, useRef } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { apiFetch } from '@/lib/api';

export interface SelectedItem {
  id: string;
  type: 'mission' | 'option';
  quantity: number;
}

const STORAGE_KEY = 'selectedMissions';
const DEBUG_FLAG_KEY = 'debug:selected-missions';
const SELECTION_POLL_INTERVAL_MS = 5000;

// Global per-clientId sync lock to prevent multiple instances from
// firing concurrent PUTs (Navigation + MissionsConfigurateur, etc.).
const _syncTimers = new Map<string, ReturnType<typeof setTimeout>>();
const _pollTimers = new Map<string, ReturnType<typeof setInterval>>();
const _inFlightFetches = new Map<
  string,
  Promise<{ items: SelectedItem[]; startedGeneration: number }>
>();
const _mutatedClients = new Set<string>();
const _mutationGenerations = new Map<string, number>();
const _refetchListeners = new Map<string, Set<() => void>>();
let _selectionVisibilityListenerRegistered = false;
let _selectedMissionsInstanceCounter = 0;

function isSelectionDebugEnabled(clientId?: string, storageKey?: string): boolean {
  if (typeof window === 'undefined') return false;

  try {
    const flag = localStorage.getItem(DEBUG_FLAG_KEY);
    if (!flag) return false;
    if (flag === 'all') return true;
    return flag === clientId || flag === storageKey;
  } catch {
    return false;
  }
}

function countMissionItems(items: SelectedItem[]): number {
  return items.filter((item) => item.type === 'mission').length;
}

function getMutationGeneration(clientId: string): number {
  return _mutationGenerations.get(clientId) ?? 0;
}

function notifySelectionRefetch(clientId: string) {
  const listeners = _refetchListeners.get(clientId);
  if (!listeners?.size) return;
  listeners.forEach((listener) => listener());
}

function startSelectionPolling(clientId: string) {
  if (_pollTimers.has(clientId) || typeof window === 'undefined') return;

  const timer = window.setInterval(() => {
    if (typeof document !== 'undefined' && document.hidden) return;
    notifySelectionRefetch(clientId);
  }, SELECTION_POLL_INTERVAL_MS);

  _pollTimers.set(clientId, timer);
}

function stopSelectionPolling(clientId: string) {
  const timer = _pollTimers.get(clientId);
  if (!timer) return;

  clearInterval(timer);
  _pollTimers.delete(clientId);
}

function registerVisibilityRefetchListener() {
  if (_selectionVisibilityListenerRegistered || typeof document === 'undefined') return;

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) return;
    _refetchListeners.forEach((_listeners, clientId) => {
      notifySelectionRefetch(clientId);
    });
  });

  _selectionVisibilityListenerRegistered = true;
}

function registerSelectionRefetch(clientId: string, refetch: () => void): () => void {
  registerVisibilityRefetchListener();
  const listeners = _refetchListeners.get(clientId) ?? new Set<() => void>();
  listeners.add(refetch);
  _refetchListeners.set(clientId, listeners);
  startSelectionPolling(clientId);

  return () => {
    const current = _refetchListeners.get(clientId);
    if (!current) return;
    current.delete(refetch);
    if (current.size === 0) {
      _refetchListeners.delete(clientId);
      stopSelectionPolling(clientId);
    }
  };
}

function selectionDebug(
  enabled: boolean,
  source: string,
  instanceId: number,
  event: string,
  payload?: Record<string, unknown>,
  withTrace: boolean = false
): void {
  if (!enabled) return;

  // eslint-disable-next-line no-console
  console.log(`[SELDBG ${source}#${instanceId}] ${event}`, payload || {});
  if (withTrace) {
    // eslint-disable-next-line no-console
    console.trace(`[SELDBG TRACE ${source}#${instanceId}] ${event}`, payload || {});
  }
}

function debouncedSyncToBackend(
  clientId: string,
  items: SelectedItem[],
  mutationGeneration: number,
  debug?: (event: string, payload?: Record<string, unknown>, withTrace?: boolean) => void
) {
  const existing = _syncTimers.get(clientId);
  if (existing) clearTimeout(existing);

  debug?.('sync:schedule', {
    totalItems: items.length,
    missionItems: countMissionItems(items),
  });

  _syncTimers.set(clientId, setTimeout(() => {
    _syncTimers.delete(clientId);
    debug?.('sync:fire', {
      totalItems: items.length,
      missionItems: countMissionItems(items),
    });
    apiFetch(`/v1/clients/${clientId}/selections`, {
      method: 'PUT',
      body: JSON.stringify({ items }),
    }).then(() => {
      const shouldHydrateFromServer = getMutationGeneration(clientId) === mutationGeneration;
      if (shouldHydrateFromServer) {
        _mutatedClients.delete(clientId);
        notifySelectionRefetch(clientId);
      }
      debug?.('sync:success', {
        totalItems: items.length,
        missionItems: countMissionItems(items),
        mutationGeneration,
        shouldHydrateFromServer,
      });
    }).catch((error) => {
      const releasedMutation = getMutationGeneration(clientId) === mutationGeneration;
      if (releasedMutation) {
        _mutatedClients.delete(clientId);
      }
      debug?.('sync:error', {
        message: error instanceof Error ? error.message : String(error),
        totalItems: items.length,
        missionItems: countMissionItems(items),
        mutationGeneration,
        releasedMutation,
      }, true);
      // Silent failure — localStorage is the fallback
    });
  }, 1000));
}

/** Valide et filtre un array parsé depuis localStorage */
function validateSelectedItems(parsed: unknown): SelectedItem[] | null {
  if (!Array.isArray(parsed)) return null;
  const valid = parsed.filter(isValidItem);
  return valid;
}

/**
 * Dédoublonne et normalise une liste d'items.
 * Conserve la première occurrence pour chaque paire (type, id) et force quantity >= 1.
 * Retourne la même référence si rien à normaliser (optimisation pour éviter des re-renders).
 */
function normalizeSelectedItems(items: SelectedItem[]): SelectedItem[] {
  const seen = new Map<string, SelectedItem>();
  let needsNormalization = false;

  for (const item of items) {
    if (!isValidItem(item)) {
      needsNormalization = true;
      continue;
    }
    const key = `${item.type}:${item.id}`;
    if (seen.has(key)) {
      needsNormalization = true;
    } else {
      const normalizedQty = Math.max(1, item.quantity);
      if (normalizedQty !== item.quantity) needsNormalization = true;
      // Preserve item reference when quantity unchanged (avoids object churn)
      seen.set(key, normalizedQty === item.quantity ? item : { ...item, quantity: normalizedQty });
    }
  }

  // Return same reference when nothing to normalize (prevents spurious re-renders and syncs)
  if (!needsNormalization) return items;
  return Array.from(seen.values());
}

function fetchSelectionSnapshot(
  clientId: string,
  debug?: (event: string, payload?: Record<string, unknown>, withTrace?: boolean) => void
): Promise<{ items: SelectedItem[]; startedGeneration: number }> {
  const existing = _inFlightFetches.get(clientId);
  if (existing) {
    debug?.('get:reuse-inflight');
    return existing;
  }

  const startedGeneration = getMutationGeneration(clientId);
  debug?.('get:start', { startedGeneration, mutated: _mutatedClients.has(clientId) });

  const request = apiFetch(`/v1/clients/${clientId}/selections`)
    .then((res) => {
      const items = normalizeSelectedItems(
        (res?.items ?? []).map((item: Record<string, unknown>) => ({
          id: (item.mission_id ?? item.id) as string,
          type: ((item.item_type ?? item.type) || 'mission') as 'mission' | 'option',
          quantity: (item.quantity as number) ?? 1,
        }))
      );

      debug?.('get:response', {
        startedGeneration,
        currentGeneration: getMutationGeneration(clientId),
        receivedCount: items.length,
        mutated: _mutatedClients.has(clientId),
      });

      return { items, startedGeneration };
    })
    .finally(() => {
      _inFlightFetches.delete(clientId);
    });

  _inFlightFetches.set(clientId, request);
  return request;
}

export const useSelectedMissions = (clientId?: string, source: string = 'useSelectedMissions') => {
  const storageKey = clientId ? `${STORAGE_KEY}_${clientId}` : STORAGE_KEY;
  const instanceIdRef = useRef<number>(++_selectedMissionsInstanceCounter);
  const debugEnabled = isSelectionDebugEnabled(clientId, storageKey);
  const debug = useCallback((event: string, payload?: Record<string, unknown>, withTrace: boolean = false) => {
    selectionDebug(debugEnabled, source, instanceIdRef.current, event, {
      clientId,
      storageKey,
      ...(payload || {}),
    }, withTrace);
  }, [clientId, debugEnabled, source, storageKey]);

  const [selectedItems, setSelectedItems] = useLocalStorage<SelectedItem[]>(
    storageKey,
    [],
    validateSelectedItems
  );

  useEffect(() => {
    debug('mount');
    return () => {
      debug('unmount');
    };
  }, [debug]);

  const fetchAndHydrate = useCallback(() => {
    if (!clientId) return Promise.resolve();

    return fetchSelectionSnapshot(clientId, debug)
      .then(({ items, startedGeneration }) => {
        const currentGeneration = getMutationGeneration(clientId);
        if (currentGeneration !== startedGeneration || _mutatedClients.has(clientId)) {
          debug('get:skip-mutated', {
            startedGeneration,
            currentGeneration,
            mutated: _mutatedClients.has(clientId),
          });
          return;
        }

        debug('get:hydrate', {
          totalItems: items.length,
          missionItems: countMissionItems(items),
          startedGeneration,
        });
        setSelectedItems(() => items);
        _mutatedClients.delete(clientId);
      })
      .catch((error) => {
        debug('get:error', {
          message: error instanceof Error ? error.message : String(error),
        }, true);
        // Fallback to localStorage silently
      });
  }, [clientId, debug, setSelectedItems]);

  useEffect(() => {
    if (!clientId) return;
    void fetchAndHydrate();
    return registerSelectionRefetch(clientId, () => {
      void fetchAndHydrate();
    });
  }, [clientId, fetchAndHydrate]);

  // Wrap setSelectedItems to also sync to backend (single global debounce per client).
  // Applies normalizeSelectedItems to guarantee a deduplicated, valid state at all times.
  const setItemsAndSync = useCallback(
    (updater: SelectedItem[] | ((prev: SelectedItem[]) => SelectedItem[])) => {
      setSelectedItems((prev) => {
        const raw = typeof updater === 'function' ? updater(prev) : updater;
        const next = normalizeSelectedItems(raw);
        const prevMissionCount = countMissionItems(prev);
        const rawMissionCount = countMissionItems(raw);
        const nextMissionCount = countMissionItems(next);
        // Only sync if the selection actually changed
        if (next !== prev && clientId) {
          const mutationGeneration = getMutationGeneration(clientId) + 1;
          _mutationGenerations.set(clientId, mutationGeneration);
          _mutatedClients.add(clientId);
          debouncedSyncToBackend(clientId, next, mutationGeneration, debug);
        }
        debug('setItemsAndSync', {
          prevLen: prev.length,
          rawLen: raw.length,
          nextLen: next.length,
          prevMissionCount,
          rawMissionCount,
          nextMissionCount,
          changed: next !== prev,
          mutationGeneration: clientId && next !== prev ? getMutationGeneration(clientId) : undefined,
        });
        if (next !== prev && nextMissionCount < prevMissionCount) {
          debug('setItemsAndSync:mission-count-decreased', {
            prevMissionCount,
            nextMissionCount,
            removedMissionIds: prev
              .filter((item) => item.type === 'mission' && !next.some((candidate) => candidate.type === 'mission' && candidate.id === item.id))
              .map((item) => item.id),
          }, true);
        }
        return next;
      });
    },
    [setSelectedItems, clientId, debug]
  );

  const addItem = (id: string, type: 'mission' | 'option', quantity: number = 1) => {
    setItemsAndSync(prev => {
      const exists = prev.find(item => item.id === id && item.type === type);
      if (exists) {
        return prev.map(item =>
          item.id === id && item.type === type ? { ...item, quantity } : item
        );
      }
      return [...prev, { id, type, quantity }];
    });
  };

  const removeItem = (id: string, type?: 'mission' | 'option') => {
    setItemsAndSync(prev =>
      prev.filter(item => type ? !(item.id === id && item.type === type) : item.id !== id)
    );
  };

  const updateQuantity = (id: string, type: 'mission' | 'option', quantity: number) => {
    setItemsAndSync(prev =>
      prev.map(item =>
        item.id === id && item.type === type
          ? { ...item, quantity: Math.max(1, quantity) }
          : item
      )
    );
  };

  const toggleItem = (id: string, type: 'mission' | 'option') => {
    setItemsAndSync(prev => {
      const exists = prev.some(item => item.id === id && item.type === type);
      if (exists) {
        return prev.filter(item => !(item.id === id && item.type === type));
      }
      return [...prev, { id, type, quantity: 1 }];
    });
  };

  const clearAll = () => {
    setItemsAndSync([]);
  };

  const isSelected = (id: string, type: 'mission' | 'option'): boolean => {
    return selectedItems.some(item => item.id === id && item.type === type);
  };

  const getSelectedMissions = () => {
    return selectedItems.filter(item => item.type === 'mission');
  };

  const getSelectedOptions = () => {
    return selectedItems.filter(item => item.type === 'option');
  };

  // Nettoyage des missions fantômes (missions supprimées côté serveur)
  const cleanupMissions = useCallback((validMissionIds: string[]) => {
    if (!Array.isArray(validMissionIds)) return;

    const validIdsSet = new Set(validMissionIds);

    debug('cleanup:start', { validCount: validMissionIds.length });
    setItemsAndSync(prev => {
      const cleaned = prev.filter(item => {
        if (item.type === 'option') return true;
        if (item.type === 'mission') return validIdsSet.has(item.id);
        return false;
      });
      debug('cleanup:result', {
        prevLen: prev.length,
        cleanedLen: cleaned.length,
        removedMissionIds: prev
          .filter((item) => item.type === 'mission' && !cleaned.some((candidate) => candidate.type === 'mission' && candidate.id === item.id))
          .map((item) => item.id),
      });
      return cleaned.length !== prev.length ? cleaned : prev;
    });
  }, [setItemsAndSync, debug]);

  return {
    selectedItems,
    addItem,
    removeItem,
    updateQuantity,
    toggleItem,
    clearAll,
    isSelected,
    getSelectedMissions,
    getSelectedOptions,
    setSelectedItems: setItemsAndSync,
    cleanupMissions
  };
};

/** Vérifie qu'un item du localStorage est valide */
function isValidItem(item: unknown): item is SelectedItem {
  if (item === null || typeof item !== 'object') return false;
  const candidate = item as Partial<SelectedItem>;
  return (
    typeof candidate.id === 'string' &&
    (candidate.type === 'mission' || candidate.type === 'option') &&
    typeof candidate.quantity === 'number'
  );
}
